import parseHtml, { domToReact, attributesToProps } from "html-react-parser";
import type { DOMNode, HTMLReactParserOptions } from "html-react-parser";
import { createElement, Fragment, type ReactNode } from "react";
import { ensureContrast, luminanceOf } from "@/src/utils/ensure-contrast";
import { VISUALLY_HIDDEN } from "@/src/utils/visually-hidden";

const toHtmlString = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(toHtmlString).join("");
  if (value === null || value === undefined) return "";
  return String(value);
};

const VALID_NAME = /^[A-Za-z][A-Za-z0-9_.:-]*$/;

const CONTENT_BACKGROUND = "#FAF9F5";
const CONTENT_BACKGROUND_DARK = "#3a3937";

const raiseInlineContrast = (style: string): string => {
  const background = style.match(
    /(?:^|;)\s*background(?:-color)?\s*:\s*([^;]+)/i,
  );

  const declaredBackground = background ? background[1].trim() : null;
  let darkColor: string | null = null;

  const raisedStyle = style.replace(
    /(^|;)(\s*color\s*:\s*)([^;]+)/gi,
    (match, separator, property, value) => {
      const color = value.trim();
      const brightness = luminanceOf(color);

      if (!declaredBackground) {
        darkColor = ensureContrast(color, CONTENT_BACKGROUND_DARK, 4.7);
        if (brightness !== null && brightness > 0.5) return match;
      }

      const raised = ensureContrast(
        color,
        declaredBackground ?? CONTENT_BACKGROUND,
      );
      return raised ? `${separator}${property}${raised}` : match;
    },
  );

  return darkColor ? `${raisedStyle};--bf-cms-ink:${darkColor}` : raisedStyle;
};

const NON_DESCRIPTIVE_LINK_TEXT = new Set([
  "click here",
  "click this",
  "go",
  "here",
  "information",
  "learn more",
  "more",
  "more info",
  "more information",
  "right here",
  "read more",
  "see more",
  "start",
  "this",
]);

type ParsedNode = {
  name?: string;
  data?: string;
  attribs?: Record<string, string>;
  children?: ParsedNode[];
  prev?: ParsedNode | null;
  parent?: ParsedNode | null;
};

const isInsideAnchor = (el: ParsedNode): boolean => {
  for (let parent = el.parent; parent; parent = parent.parent ?? null) {
    if (parent.name === "a") return true;
  }
  return false;
};

const dropColor = (style: string): string =>
  style
    .replace(/(^|;)\s*color\s*:[^;]*/gi, "$1")
    .replace(/;{2,}/g, ";")
    .replace(/^;|;$/g, "")
    .trim();

const textOf = (node: ParsedNode): string => {
  if (node.name === undefined && typeof node.data === "string")
    return node.data;
  return (node.children ?? []).map(textOf).join("");
};

const boldTextWithin = (node: ParsedNode): string | null => {
  for (const child of node.children ?? []) {
    if (child.name === "strong" || child.name === "b") {
      const text = textOf(child).trim();
      if (text) return text;
    }
    const nested = boldTextWithin(child);
    if (nested) return nested;
  }
  return null;
};

const trimLabelPunctuation = (label: string | null): string | null =>
  label === null ? null : label.replace(/[:：\s]+$/, "");

const precedingLabel = (el: ParsedNode): string | null => {
  for (let sibling = el.prev; sibling; sibling = sibling.prev ?? null) {
    if (sibling.name === "strong" || sibling.name === "b") {
      const text = textOf(sibling).trim();
      if (text) return text;
    }
    const nested = boldTextWithin(sibling);
    if (nested) return nested;
  }
  return null;
};

const labelFromHref = (href?: string): string | null => {
  if (!href) return null;
  const path = href.split("?")[0];
  const segment = path.includes("#")
    ? path.split("#").pop()
    : path.replace(/\/+$/, "").split("/").pop();
  if (!segment) return null;
  try {
    return (
      decodeURIComponent(segment)
        .replace(/[-_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim() || null
    );
  } catch {
    return null;
  }
};

const imageLabel = (el: ParsedNode): string | null => {
  const stack = [...(el.children ?? [])];
  while (stack.length) {
    const child = stack.shift() as ParsedNode;
    if (child.name === "img") {
      const named = child.attribs?.alt?.trim() || child.attribs?.title?.trim();
      if (named) return named;
    }
    if (child.children) stack.push(...child.children);
  }
  return null;
};

const sanitizeOptions: HTMLReactParserOptions = {
  replace: (node) => {
    const el = node as {
      name?: string;
      attribs?: Record<string, string>;
      children?: DOMNode[];
    };

    if (!el.attribs || el.name === undefined) {
      return undefined;
    }

    if (!VALID_NAME.test(el.name)) {
      return createElement(
        Fragment,
        null,
        domToReact(el.children ?? [], sanitizeOptions),
      );
    }

    for (const name of Object.keys(el.attribs)) {
      if (!VALID_NAME.test(name)) {
        delete el.attribs[name];
      }
    }

    if (
      typeof el.attribs.style === "string" &&
      /(^|;)\s*color\s*:/i.test(el.attribs.style)
    ) {
      el.attribs.style = raiseInlineContrast(el.attribs.style);
    }

    if (el.name !== "a" && isInsideAnchor(el as ParsedNode)) {
      const remaining = dropColor(el.attribs.style ?? "");
      el.attribs.style = remaining
        ? `${remaining};display:inline`
        : "display:inline";
    }

    if (el.name === "a" && !/text-decoration/i.test(el.attribs.style ?? "")) {
      const existing = (el.attribs.style ?? "").trim().replace(/;$/, "");
      el.attribs.style = existing
        ? `${existing};text-decoration:underline`
        : "text-decoration:underline";
    }

    if (el.name === "a" && !/padding-block/i.test(el.attribs.style ?? "")) {
      const existing = (el.attribs.style ?? "").trim().replace(/;$/, "");
      el.attribs.style = existing
        ? `${existing};padding-block:0.3125rem`
        : "padding-block:0.3125rem";
    }

    if (el.name === "img" && el.attribs.fetchpriority) {
      const props = attributesToProps(el.attribs, el.name) as Record<
        string,
        unknown
      >;
      delete props.fetchpriority;
      props.fetchPriority = el.attribs.fetchpriority;
      return createElement("img", props);
    }

    if (el.name === "a") {
      const visible = textOf(el as ParsedNode).trim();
      if (!visible || NON_DESCRIPTIVE_LINK_TEXT.has(visible.toLowerCase())) {
        const label = visible
          ? (trimLabelPunctuation(precedingLabel(el as ParsedNode)) ??
            labelFromHref(el.attribs.href))
          : (imageLabel(el as ParsedNode) ?? labelFromHref(el.attribs.href));
        if (label) {
          return createElement(
            "a",
            attributesToProps(el.attribs, el.name),
            createElement(
              Fragment,
              null,
              domToReact(el.children ?? [], sanitizeOptions),
            ),
            createElement("span", { style: VISUALLY_HIDDEN }, ` ${label}`),
          );
        }
      }
    }

    return undefined;
  },
};

/*
 * CMS bodies predate the broadsheet ICON RULE and still carry Material Icons
 * ligatures (`<i class="material-icons-round">public</i>`). Two of them carry
 * meaning and become real characters; the rest are decorative and are dropped.
 * With no surface rendering the ligatures, the icon font no longer ships.
 */
const ICON_LIGATURE_TEXT: Record<string, string> = {
  alternate_email: "@",
  copyright: "©",
};

export const replaceIconLigatures = (html: string): string =>
  html.replace(
    /<i\b[^>]*material-icons[^>]*>([^<]*)<\/i>/gi,
    (match, ligature) => ICON_LIGATURE_TEXT[ligature.trim()] ?? "",
  );

const ReactHtmlParser = (value: unknown): ReactNode =>
  parseHtml(replaceIconLigatures(toHtmlString(value)), sanitizeOptions);

export default ReactHtmlParser;

export const addHeadingIds = (html: string): string => {
  const headingRegex = /<h([1-6])(.*?)>([\s\S]*?)<\/h\1>/g;
  let sectionNumber = 1;

  const modifiedHtml = html.replace(headingRegex, (match, p1, p2, p3) => {
    const labelContent = p3.trim().replace(/"/g, "&quot;");
    const authoredId = /(?:^|\s)id\s*=\s*(?:"([^"]*)"|'([^']*)')/i.exec(p2);
    const hasAuthoredId = !!(authoredId?.[1] || authoredId?.[2]);
    const generatedId = hasAuthoredId ? "" : ` id="section-${sectionNumber}"`;
    sectionNumber++;
    return `<h${p1}${generatedId}${p2} label="${labelContent}">${p3}</h${p1}>`;
  });

  return modifiedHtml;
};
